import React from 'react';
import { Button, IconButton, Picker, TextInput, withTheme } from '@draftbit/ui';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { ActivityIndicator, Text, View } from 'react-native';
import { Fetch } from 'react-request';
import * as GlobalStyles from '../GlobalStyles.js';
import * as DefaultApi from '../apis/DefaultApi.js';
import * as GlobalVariables from '../config/GlobalVariableContext';
import checkScreenMode from '../global-functions/checkScreenMode';
import palettes from '../themes/palettes';
import Breakpoints from '../utils/Breakpoints';
import * as StyleSheet from '../utils/StyleSheet';
import useWindowDimensions from '../utils/useWindowDimensions';
import waitUtil from '../utils/wait';

const ScenarioBlock = props => {
  const { theme } = props;
  const dimensions = useWindowDimensions();
  const navigation = useNavigation();
  const Constants = GlobalVariables.useValues();
  const Variables = Constants;
  const [buttonLabel, setButtonLabel] = React.useState('Create New');
  const [isFetching, setIsFetching] = props.setIsFetching
    ? [
        props.isFetching !== undefined ? props.isFetching : false,
        props.setIsFetching,
      ]
    : React.useState(false);
  const [isLoading, setIsLoading] = props.setIsLoading
    ? [
        props.isLoading !== undefined ? props.isLoading : false,
        props.setIsLoading,
      ]
    : React.useState(false);
  const [pickerValue, setPickerValue] = React.useState('');
  const [reFetch, setReFetch] = props.setReFetch
    ? [props.reFetch !== undefined ? props.reFetch : false, props.setReFetch]
    : React.useState(false);
  const [scenarioTitle, setScenarioTitle] = React.useState('');
  const [toggleCreateNew, setToggleCreateNew] = React.useState(false);
  const [visibleWhatIf, setVisibleWhatIf] = props.setVisibleWhatIf
    ? [
        props.visibleWhatIf !== undefined ? props.visibleWhatIf : false,
        props.setVisibleWhatIf,
      ]
    : React.useState(false);
  const getOptions = data => {
    return data.map(item => ({
      label: item.title,
      value: item.id,
    }));
  };
  const defaultCreateNewScenarioPOST = DefaultApi.useCreateNewScenarioPOST();

  return (
    <View
      style={StyleSheet.applyWidth(
        {
          backgroundColor: {
            minWidth: Breakpoints.Laptop,
            value: theme.colors.background.brand,
          },
          borderColor: {
            minWidth: Breakpoints.Laptop,
            value: palettes.App['Corner Gradient Dark'],
          },
          borderRadius: { minWidth: Breakpoints.Laptop, value: 8 },
          borderWidth: { minWidth: Breakpoints.Laptop, value: 1 },
          flex: { minWidth: Breakpoints.Laptop, value: 1 },
          marginBottom: {
            minWidth: Breakpoints.Laptop,
            value: dimensions.height * (33 / 100),
          },
          marginLeft: { minWidth: Breakpoints.Laptop, value: '25%' },
          marginRight: { minWidth: Breakpoints.Laptop, value: '25%' },
          marginTop: {
            minWidth: Breakpoints.Laptop,
            value: dimensions.height * (33 / 100),
          },
          minHeight: { minWidth: Breakpoints.Laptop, value: 350 },
          padding: { minWidth: Breakpoints.Laptop, value: 24 },
        },
        dimensions.width
      )}
    >
      {/* Header */}
      <View
        style={StyleSheet.applyWidth(
          {
            alignItems: { minWidth: Breakpoints.Laptop, value: 'center' },
            flexDirection: { minWidth: Breakpoints.Laptop, value: 'row' },
          },
          dimensions.width
        )}
      >
        {/* Left */}
        <View
          style={StyleSheet.applyWidth(
            {
              flex: [
                { minWidth: Breakpoints.Desktop, value: 1 },
                { minWidth: Breakpoints.Laptop, value: 1 },
              ],
            },
            dimensions.width
          )}
        />
        {/* Middle */}
        <View
          style={StyleSheet.applyWidth(
            {
              alignItems: [
                { minWidth: Breakpoints.Desktop, value: 'center' },
                { minWidth: Breakpoints.Laptop, value: 'center' },
              ],
              flex: { minWidth: Breakpoints.Laptop, value: 5 },
              justifyContent: [
                { minWidth: Breakpoints.Desktop, value: 'center' },
                { minWidth: Breakpoints.Laptop, value: 'center' },
              ],
            },
            dimensions.width
          )}
        >
          {/* Header Text */}
          <Text
            accessible={true}
            selectable={false}
            {...GlobalStyles.TextStyles(theme)['Header'].props}
            style={StyleSheet.applyWidth(
              StyleSheet.compose(
                GlobalStyles.TextStyles(theme)['Header'].style,
                {
                  fontSize: { minWidth: Breakpoints.Laptop, value: 20 },
                  textAlign: { minWidth: Breakpoints.Laptop, value: 'center' },
                }
              ),
              dimensions.width
            )}
          >
            {'What If Scenarios'}
          </Text>
        </View>
        {/* Right */}
        <View
          style={StyleSheet.applyWidth(
            {
              alignItems: { minWidth: Breakpoints.Laptop, value: 'flex-end' },
              flex: { minWidth: Breakpoints.Laptop, value: 1 },
            },
            dimensions.width
          )}
        >
          <IconButton
            onPress={() => {
              try {
                setVisibleWhatIf(false);
                setToggleCreateNew(false);
                setScenarioTitle('');
              } catch (err) {
                console.error(err);
              }
            }}
            size={32}
            color={palettes.Brand['App Red']}
            icon={'Feather/x'}
          />
        </View>
      </View>
      {/* Scenarios Picker */}
      <>
        {toggleCreateNew ? null : (
          <View
            style={StyleSheet.applyWidth(
              {
                flex: { minWidth: Breakpoints.Laptop, value: 1 },
                marginTop: { minWidth: Breakpoints.Laptop, value: 40 },
              },
              dimensions.width
            )}
          >
            <DefaultApi.FetchGetAllScenarioGET>
              {({ loading, error, data, refetchGetAllScenario }) => {
                const fetchData = data?.json;
                if (loading) {
                  return <ActivityIndicator />;
                }

                if (error || data?.status < 200 || data?.status >= 300) {
                  return <ActivityIndicator />;
                }

                return (
                  <Picker
                    autoDismissKeyboard={true}
                    dropDownBackgroundColor={theme.colors.background.base}
                    dropDownBorderColor={theme.colors.border.base}
                    dropDownBorderRadius={8}
                    dropDownBorderWidth={1}
                    dropDownTextColor={theme.colors.text.strong}
                    iconSize={24}
                    leftIconMode={'inset'}
                    mode={'native'}
                    onValueChange={newPickerValue => {
                      const pickerValue = newPickerValue;
                      try {
                        setPickerValue(
                          (() => {
                            const e = newPickerValue;
                            console.log(e);
                            return e;
                          })()
                        );
                      } catch (err) {
                        console.error(err);
                      }
                    }}
                    selectedIconColor={theme.colors.text.strong}
                    selectedIconName={'Feather/check'}
                    selectedIconSize={20}
                    type={'solid'}
                    options={getOptions(fetchData)}
                    placeholder={'Select a scenario'}
                    rightIconName={'AntDesign/caretdown'}
                    style={StyleSheet.applyWidth(
                      {
                        color: {
                          minWidth: Breakpoints.Laptop,
                          value: palettes.Brand.White,
                        },
                      },
                      dimensions.width
                    )}
                    value={pickerValue}
                  />
                );
              }}
            </DefaultApi.FetchGetAllScenarioGET>
          </View>
        )}
      </>
      {/* New Scenarios Form */}
      <>
        {!toggleCreateNew ? null : (
          <View
            style={StyleSheet.applyWidth(
              {
                flex: { minWidth: Breakpoints.Laptop, value: 1 },
                marginTop: { minWidth: Breakpoints.Laptop, value: 40 },
              },
              dimensions.width
            )}
          >
            <TextInput
              autoCapitalize={'none'}
              autoCorrect={true}
              changeTextDelay={500}
              onChangeText={newTextInputValue => {
                try {
                  setScenarioTitle(newTextInputValue);
                } catch (err) {
                  console.error(err);
                }
              }}
              webShowOutline={true}
              {...GlobalStyles.TextInputStyles(theme)['Text Input'].props}
              placeholder={'Enter scenario name'}
              style={StyleSheet.applyWidth(
                StyleSheet.compose(
                  GlobalStyles.TextInputStyles(theme)['Text Input'].style,
                  {
                    paddingBottom: { minWidth: Breakpoints.Laptop, value: 12 },
                    paddingTop: { minWidth: Breakpoints.Laptop, value: 12 },
                  }
                ),
                dimensions.width
              )}
              value={scenarioTitle}
            />
          </View>
        )}
      </>
      {/* Button */}
      <View
        style={StyleSheet.applyWidth(
          { gap: { minWidth: Breakpoints.Laptop, value: 20 } },
          dimensions.width
        )}
      >
        {/* Continue button */}
        <>
          {!pickerValue ? null : (
            <Button
              accessible={true}
              iconPosition={'left'}
              onPress={() => {
                const handler = async () => {
                  try {
                    setVisibleWhatIf(false);
                    navigation.navigate('WhatIfScreen', {
                      scenario: pickerValue,
                      new_or_exist: 'exist',
                      exist_scenario_id: pickerValue,
                    });
                    setIsLoading(true);
                    setIsFetching(true);
                    setReFetch(false);
                    await waitUtil({ milliseconds: 500 });
                    setIsFetching(false);
                  } catch (err) {
                    console.error(err);
                  }
                };
                handler();
              }}
              {...GlobalStyles.ButtonStyles(theme)['Button'].props}
              style={StyleSheet.applyWidth(
                StyleSheet.compose(
                  GlobalStyles.ButtonStyles(theme)['Button'].style,
                  {
                    backgroundColor: {
                      minWidth: Breakpoints.Laptop,
                      value: palettes.Brand['App Blue Dark'],
                    },
                  }
                ),
                dimensions.width
              )}
              title={'Continue'}
            />
          )}
        </>
        {/* Create New Button */}
        <Button
          accessible={true}
          iconPosition={'left'}
          onPress={() => {
            const handler = async () => {
              try {
                setPickerValue('');
                if (buttonLabel === 'Create') {
                  const dataRes = (
                    await defaultCreateNewScenarioPOST.mutateAsync({
                      title: scenarioTitle,
                    })
                  )?.json;
                  setVisibleWhatIf(false);
                  navigation.navigate('WhatIfScreen', {
                    scenario: dataRes,
                    new_or_exist: 'new',
                  });
                  setIsLoading(true);
                  setIsFetching(true);
                  await waitUtil({ milliseconds: 500 });
                  setReFetch(false);
                  setIsFetching(false);
                } else {
                  setToggleCreateNew(true);
                  setButtonLabel('Create');
                }
              } catch (err) {
                console.error(err);
              }
            };
            handler();
          }}
          {...GlobalStyles.ButtonStyles(theme)['Button'].props}
          style={StyleSheet.applyWidth(
            StyleSheet.compose(
              GlobalStyles.ButtonStyles(theme)['Button'].style,
              {
                backgroundColor: [
                  {
                    minWidth: Breakpoints.Desktop,
                    value: palettes.Brand['App Green'],
                  },
                  {
                    minWidth: Breakpoints.Laptop,
                    value: palettes.Brand['App Green'],
                  },
                ],
                fontSize: { minWidth: Breakpoints.Laptop, value: 16 },
              }
            ),
            dimensions.width
          )}
          title={`${buttonLabel}`}
        />
      </View>
    </View>
  );
};

export default withTheme(ScenarioBlock);
